'use client'

// The net under everything. React's minified "error #185" screen names
// nothing and swallows the component stack, which made an intermittent
// update-depth loop undebuggable. This boundary prints the stack (with
// sourcemaps on, devtools resolves it to real files) and shows a card a
// person can act on instead of a dead black page.

import { Component, Fragment } from 'react'

/**
 * The six facts that decide every "works for me" report.
 *
 * Gathered at crash time rather than logged at boot, because the person
 * who hits this is on a browser we do not have, and a screenshot of the
 * card has to be enough to act on. The WebGL strings are what actually
 * name a driver: UNMASKED_RENDERER is the difference between "Firefox"
 * and "Firefox on a blocklisted Intel GPU".
 */
function diagnostics() {
  const out = { renderer: (typeof window !== 'undefined' && window.__renderer) || 'never initialised' }
  try {
    out.ua = navigator.userAgent
    out.gpuApi = typeof navigator.gpu === 'undefined' ? 'no navigator.gpu' : 'navigator.gpu present'
    const c = document.createElement('canvas')
    const gl = c.getContext('webgl2') || c.getContext('webgl')
    if (!gl) {
      out.webgl = 'no webgl context at all'
    } else {
      const dbg = gl.getExtension('WEBGL_debug_renderer_info')
      out.webgl = gl.getParameter(gl.VERSION)
      out.gpu = dbg ? gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) : '(masked)'
    }
  } catch (err) {
    out.probeFailed = err?.message || String(err)
  }
  return out
}

export default class CrashGuard extends Component {
  state = { broken: false, resets: 0, why: null }

  static getDerivedStateFromError(error) {
    // The message is kept, not just the flag. A friendly card that says
    // nothing turns a fixable bug into "it doesn't work on Firefox", and
    // the person who can see the error is never the person who can read
    // a console.
    return { broken: true, why: error?.message ? String(error.message).slice(0, 300) : null }
  }

  componentDidCatch(error, info) {
    // The whole point: the component stack names the looping component,
    // which the default production error screen never does. The tag says
    // which boundary caught it (world vs page) before anyone reads frames.
    const tag = this.props.tag ? ` (${this.props.tag})` : ''
    console.error(`[crash]${tag}`, error?.message || error)
    // The THROW stack, not just the component stack. The wall-loader loop
    // was only findable because a paste happened to include the uncaught
    // error's own frames (loader → progress → setState); the boundary now
    // keeps that path itself, plus how long the page had been up — "8s"
    // reads as during-load, "400s" reads as mid-session.
    console.error(`[crash]${tag} threw at ${Math.round(performance.now() / 1000)}s uptime, via:`, error?.stack || '(no stack)')
    console.error(`[crash]${tag} component stack:`, info?.componentStack || '(none)')
    console.error(`[crash]${tag} where:`, diagnostics())
    // Self-healing subtrees: a password manager or wallet extension jamming
    // its own nodes into ours makes React's next insertBefore/removeChild
    // throw — the tree is fine, the DOM was vandalised. A clean remount
    // fixes it invisibly. Three strikes, then it stays down like any other
    // crash (a genuine render bug would otherwise heal-loop forever).
    if (this.props.selfHeal && this.state.resets < 3) {
      setTimeout(() => this.setState((s) => ({ broken: false, resets: s.resets + 1 })), 60)
    }
  }

  render() {
    if (!this.state.broken) {
      // The key makes each heal a full remount, not a patch-up of the
      // same mangled nodes.
      return <Fragment key={this.state.resets}>{this.props.children}</Fragment>
    }
    // A tagged (inner) boundary renders nothing rather than the full-page
    // card: the world dying should leave the drawers usable, and the root
    // boundary still catches anything truly global.
    if (this.props.tag) return null
    return (
      <div
        style={{
          position: 'fixed', inset: 0, display: 'flex', alignItems: 'center',
          justifyContent: 'center', background: '#1a100a', zIndex: 9999,
          fontFamily: 'var(--font-rounded), ui-rounded, system-ui, sans-serif',
        }}
      >
        <div
          style={{
            background: '#f7e7d2', border: '2.5px solid #2f2650', borderRadius: 18,
            boxShadow: '0 4px 0 rgba(47,38,80,0.32)', padding: '22px 26px',
            maxWidth: 380, textAlign: 'center',
          }}
        >
          <p style={{ color: '#2f2650', fontSize: 16, fontWeight: 800, marginBottom: 6 }}>
            something in the town tripped over itself
          </p>
          <p style={{ color: 'rgba(47,38,80,0.62)', fontSize: 13.5, fontWeight: 600, lineHeight: 1.5, marginBottom: 14 }}>
            he is fine, the page is not. a reload puts it right.
          </p>
          {/* Quiet, secondary, and the whole reason a browser we do not
              have can still be fixed: one button, one paste, and the
              report contains the error AND the driver that hit it. */}
          <button
            type="button"
            onClick={() => {
              const report = [
                this.state.why || '(no message)',
                ...Object.entries(diagnostics()).map(([k, v]) => `${k}: ${v}`),
              ].join('\n')
              navigator.clipboard?.writeText(report).then(
                () => this.setState({ copied: true }),
                () => this.setState({ copied: false, showRaw: report }),
              )
            }}
            style={{
              background: 'none', border: 'none', padding: 0, marginBottom: 12,
              color: 'rgba(47,38,80,0.55)', fontSize: 12, fontWeight: 700,
              textDecoration: 'underline', cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            {this.state.copied ? 'copied, send it to sam' : 'copy what went wrong'}
          </button>
          {this.state.showRaw && (
            <pre
              style={{
                textAlign: 'left', fontSize: 10.5, lineHeight: 1.45, maxHeight: 130,
                overflow: 'auto', background: 'rgba(47,38,80,0.07)', borderRadius: 10,
                padding: '8px 10px', marginBottom: 12, whiteSpace: 'pre-wrap',
                color: 'rgba(47,38,80,0.75)',
              }}
            >
              {this.state.showRaw}
            </pre>
          )}
          <button
            type="button"
            onClick={() => window.location.reload()}
            style={{
              background: '#f0b064', color: '#2f2650', border: '2.5px solid #2f2650',
              borderRadius: 999, boxShadow: '0 3px 0 rgba(47,38,80,0.3)',
              padding: '7px 22px', fontSize: 14, fontWeight: 800, cursor: 'pointer',
            }}
          >
            reload
          </button>
        </div>
      </div>
    )
  }
}
